import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineBookmark,
    MdOutlineBookmarkBorder,
    MdOutlineCalculate,
    MdOutlineKeyboard,
    MdOutlineLocationOn,
    MdOutlinePlayCircle,
    MdSuperscript
} from 'react-icons/md';
import moment from 'moment';
import Button from 'components/Button';
import TextInput from 'components/TextInput';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import { MULTIPLE_CHOICES } from 'global/constants';
import { getDeviceOS } from 'global/os';
import { generatePassageHTML } from 'global/passageHighlight';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Reference from '../Digital/Module/Reference';
import Shortcuts from '../Digital/Module/Shortcuts';
import Helmet from 'components/Helmet';
import MathOpenEnded from '../Digital/Module/MathOpenEnded';
import DesmosCalculatorPanel from 'components/DesmosCalculatorPanel';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import Explanation from '../Digital/Module/Explanation';
import { flattenKatexUnderlines } from 'global/katexWrap';

const COLORS = {
    'Yellow': 'yellow',
    'Pink': 'pink',
    'Blue': 'blue'
};

const UNDERLINE = {
    'Solid': 'solid',
    'Dashed': 'dashed',
    'Dotted': 'dotted'
};

function QuizReview() {
    const [studentName, setStudentName] = useState('');
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [referenceOpen, setReferenceOpen] = useState(false);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [shortcutKeyPressed, setShortcutKeyPressed] = useState(false);
    const [annotations, setAnnotations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [quizName, setQuizName] = useState('');
    const [quizId, setQuizId] = useState('');
    const [subject, setSubject] = useState('');
    const [quizPrompt, setQuizPrompt] = useState('');
    const [moduleBreak, setModuleBreak] = useState(false);
    const [questions, setQuestions] = useState(null);
    const [answers, setAnswers] = useState([]);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { testId, courseUrl, quizNum } = useParams();
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < questions.length-1){
            setQuestionIndex(questionIndex + 1);
        }
        else {
            // exit the review
            navigate(-1);
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let passages = document.getElementById('quizReview__passages');
        let question = document.getElementById('quizReview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    }

    const generateAnnotation = () => {
        const newAnnotations = [ ...answers[questionIndex].annotations ];

        for (let i = 0; i < newAnnotations.length; i++) {
            const {
                passageId,
                startIndex,
                endIndex
            } = newAnnotations[i];

            const passage = document.getElementById(passageId);

            // Stem annotations reference a question id with no matching
            // passage element on the review page; skip this one rather than
            // abandoning the remaining annotations.
            if (!passage) continue;

            const { passageHTML, passagePreview } = generatePassageHTML(passage, startIndex, endIndex);

            newAnnotations[i].html = passageHTML;
            newAnnotations[i].preview = passagePreview;
        }

        setAnnotations(newAnnotations);
    }

    const handleAnnotationMouseOver = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        annotationId: string
    ) => {
        let bubble = document.getElementById(annotationId);

        if (bubble) {
            bubble.style.left = event.clientX + 'px';
            bubble.style.top = event.clientY + 'px';
            bubble.style.display = 'block';
        }
        return false;
    }

    const handleAnnotationMouseOut = (
        _event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        annotationId: string
    ) => {
        let bubble = document.getElementById(annotationId);

        if (bubble) {
            bubble.style.display = 'none';
        }
        return false;
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            if (subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const handleWindowsShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            code
        } = event;

        if (ctrlKey && altKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (ctrlKey && altKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                case 'KeyR':
                    if (subject === 'Math') {
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        if (showExplanation) setShowExplanation(false);
                        setReferenceOpen(!referenceOpen);
                    }
                    break;
                case 'KeyC':
                    if (subject === 'Math') {
                        if (referenceOpen) setReferenceOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        if (showExplanation) setShowExplanation(false);
                        setCalculatorOpen(!calculatorOpen);
                    }
                    break;
                case 'KeyW':
                    if (referenceOpen) setReferenceOpen(false);
                    if (shortcutsOpen) setShortcutsOpen(false);
                    if (calculatorOpen) setCalculatorOpen(false);
                    setShowExplanation(!showExplanation);
                    break;
                default:
                    return;
            }
            
        }
        else if (ctrlKey && shiftKey) {
            switch(code) {
                // case 'Digit1':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit2':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const handleMacShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            metaKey,
            code
        } = event;

        if (metaKey && ctrlKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && ctrlKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && altKey) {
            switch(code) {
                case 'KeyH':
                    if (subject === 'Math') {
                        if (calculatorOpen) setCalculatorOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        if (showExplanation) setShowExplanation(false);
                        setReferenceOpen(!referenceOpen);
                    }
                    break;
                case 'KeyC':
                    if (subject === 'Math') {
                        if (referenceOpen) setReferenceOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        if (showExplanation) setShowExplanation(false);
                        setCalculatorOpen(!calculatorOpen);
                    }
                    break;
                case 'KeyW':
                        if (referenceOpen) setReferenceOpen(false);
                        if (shortcutsOpen) setShortcutsOpen(false);
                        if (calculatorOpen) setCalculatorOpen(false);
                        setShowExplanation(!showExplanation);
                        break;
                default:
                    return;
            }
            
        }
        else if (metaKey && shiftKey) {
            switch(code) {
                // case 'KeyV':
                //     markQuestion();
                //     break;
                default:
                    return;
            }
        }
        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    // const dragMove = (element: HTMLElement, header: HTMLElement) => {
    //     let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

    //     const dragMouseDown = (event) => {
    //         event = event || window.event;
    //         event.preventDefault();
    
    //         pos3 = event.clientX;
    //         pos4 = event.clientY;
    //         document.onmouseup = closeDragElement;
    //         document.onmousemove = dragElement;
    //     }

    //     const dragElement = (event) => {
    //         event = event || window.event;
    //         event.preventDefault();

    //         pos1 = pos3 - event.clientX;
    //         pos2 = pos4 - event.clientY;
    //         pos3 = event.clientX;
    //         pos4 = event.clientY;

    //         element.style.top = (element.offsetTop - pos2) + 'px';
    //         element.style.left = (element.offsetLeft - pos1) + 'px';
    //     }

    //     const closeDragElement = () => {
    //         document.onmouseup = null;
    //         document.onmousemove = null;
    //     }
    
    //     header.onmousedown = dragMouseDown;
    //     header.ontouchstart = dragMouseDown;
    // }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
    
        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        };
    
        const dragElement = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }        
        };
    
        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.quizReview__popupHeaderIcon') || event.target.closest('.quizReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.quizReview__popupHeaderIcon') || event.target.closest('.quizReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    };

    useEffect(() => {
        axios.get(`courses/classroom/${courseUrl}/quiz/${quizNum}/review/`)
            .then(res => {
                setStudentName(res.data.studentName);
                setQuizName(res.data.name);
                setQuizId(res.data.id);
                setSubject(res.data.subject);
                setQuizPrompt(res.data.prompt);
                setModuleBreak(false);
                setQuestions(res.data.questions);
                setAnswers(res.data.answers);
                setQuestionIndex(0);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }, []);

    useEffect(() => {
        if (questions !== null) {
            setShortcutKeyPressed(true);

            // dragMove(document.getElementById('calculatorDrag'), document.getElementById('calculatorHeader'));
            // dragMove(document.getElementById('referenceDrag'), document.getElementById('referenceHeader'));
            // dragMove(document.getElementById('shortcutsDrag'), document.getElementById('shortcutsHeader'));
            // dragMove(document.getElementById('explanationDrag'), document.getElementById('explanationHeader'));
            const referenceDragEl = document.getElementById('referenceDrag');
            const referenceHeaderEl = document.getElementById('referenceHeader');
            if (referenceDragEl && referenceHeaderEl) {
                dragMove(referenceDragEl, referenceHeaderEl);
            }
        
            const shortcutsDragEl = document.getElementById('shortcutsDrag');
            const shortcutsHeaderEl = document.getElementById('shortcutsHeader');
            if (shortcutsDragEl && shortcutsHeaderEl) {
                dragMove(shortcutsDragEl, shortcutsHeaderEl);
            }

            const explanationDragEl = document.getElementById('explanationDrag');
            const explanationHeaderEl = document.getElementById('explanationHeader');
            if (explanationDragEl && explanationHeaderEl) {
                dragMove(explanationDragEl, explanationHeaderEl);
            }
        }
    }, [questions]);

    useLayoutEffect(() => {
        if (questions!== null) {
            renderKatex();
            generateAnnotation();
        }
    }, [questions, questionIndex]);

    useEffect(() => {
        if (questions!== null) {
            renderKatex();
        }
    }, [directionsOpen, annotations]);

    useEffect(() => {
        if (getDeviceOS() === 'windows') {
            document.addEventListener('keydown', handleWindowsShortcutKeys);
        }
        else if (getDeviceOS() === 'mac_os') {
            document.addEventListener('keydown', handleMacShortcutKeys);
        }

        return () => {
            if (getDeviceOS() === 'windows') {
                document.removeEventListener('keydown', handleWindowsShortcutKeys);
            }
            else if (getDeviceOS() === 'mac_os') {
                document.removeEventListener('keydown', handleMacShortcutKeys);
            }
        }
    }, [shortcutKeyPressed]);

    if (questions === null) return <Loader />;

    return (
        <div className='quizReviewBox' onCopy={(event) => { event.preventDefault() }}>
            <Helmet
                title={`${quizName ? quizName : 'Digital SAT Practice Test'} - The Digital SAT Crash Course`}
            />
            <div className='quizReview'>
                <div className='quizReview__header'>
                    <div className='quizReview__titleGroup'>
                        <button className='quizReview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='quizReview__directionsIcon' />
                                : <MdKeyboardArrowDown className='quizReview__directionsIcon' />
                            }
                        </button>
                    </div>
                    <div className='quizReview__timerGroup'>
                        <p className='quizReview__timer'>
                            {quizName}
                        </p>
                    </div>
                    <div className='quizReview__actionGroup'>
                        {subject === 'Math' && <>
                            <button
                                className={referenceOpen
                                    ? 'quizReview__action--active'
                                    : 'quizReview__action'
                                }
                                onClick={() => {
                                    if (calculatorOpen) setCalculatorOpen(false);
                                    if (shortcutsOpen) setShortcutsOpen(false);
                                    if (showExplanation) setShowExplanation(false);
                                    setReferenceOpen(!referenceOpen);
                                }}
                            >
                                <MdSuperscript className='quizReview__actionIcon' />
                                <p className='quizReview__actionText'>Reference</p>
                            </button>
                            <button 
                                className={calculatorOpen
                                    ? 'quizReview__action--active'
                                    : 'quizReview__action'
                                }
                                onClick={() => {
                                    if (referenceOpen) setReferenceOpen(false);
                                    if (shortcutsOpen) setShortcutsOpen(false);
                                    if (showExplanation) setShowExplanation(false);
                                    setCalculatorOpen(!calculatorOpen);
                                }}
                            >
                                <MdOutlineCalculate className='quizReview__actionIcon' />
                                <p className='quizReview__actionText'>Calculator</p>
                            </button>
                        </>}
                        <button
                            className={shortcutsOpen
                                ? 'quizReview__action--active'
                                : 'quizReview__action'
                            }
                            onClick={() => {
                                if (calculatorOpen) setCalculatorOpen(false);
                                if (referenceOpen) setReferenceOpen(false);
                                if (showExplanation) setShowExplanation(false);
                                setShortcutsOpen(!shortcutsOpen);
                            }}
                        >
                            <MdOutlineKeyboard className='quizReview__actionIcon' />
                            <p className='quizReview__actionText'>Shortcuts</p>
                        </button>
                        <button 
                            className='quizReview__action' 
                            onClick={() => navigate(-1)}
                        >
                            <MdExitToApp className='quizReview__actionIcon' />
                            <p className='quizReview__actionText'>Exit</p>
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='quizReview__directions' ref={directionsRef}>
                            <div className='quizReview__directionsBubble'>
                                <p className='katex quizReview__directionsText'>
                                    {quizPrompt
                                        ? quizPrompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='quizReview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }
                    <DesmosCalculatorPanel
                        open={calculatorOpen}
                        onClose={() => setCalculatorOpen(false)}
                        headerClassName='quizReview__popupHeader'
                    />
                    <div id='referenceDrag' className={referenceOpen
                        ? 'quizReview__popup--reference'
                        : 'quizReview__popup--hidden'
                    }>
                        <div id='referenceHeader' className='quizReview__popupHeader'>
                            <h3 className='quizReview__popupHeaderText'>Reference</h3>
                            <MdClose className='quizReview__popupHeaderIcon' onClick={() => setReferenceOpen(false)} />
                        </div>
                        <Reference />
                    </div>
                    <div id='shortcutsDrag' className={shortcutsOpen
                        ? 'quizReview__popup--shortcuts'
                        : 'quizReview__popup--hidden'
                    }>
                        <div id='shortcutsHeader' className='quizReview__popupHeader'>
                            <h3 className='quizReview__popupHeaderText'>
                                Keyboard shortcuts{getDeviceOS() === 'windows'
                                    ? ': Windows'
                                    : getDeviceOS() === 'mac_os'
                                    ? ': MacOS'
                                    : ''
                                }
                            </h3>    
                            <MdClose className='quizReview__popupHeaderIcon' onClick={() => setShortcutsOpen(false)} />
                        </div>
                        <Shortcuts />
                    </div>
                    <div id='explanationDrag' className={showExplanation
                        ? 'quizReview__popup--explanation'
                        : 'quizReview__popup--hidden'
                    }>
                        <div id='explanationHeader' className='quizReview__popupHeader'>
                            <h3 className='quizReview__popupHeaderText'>Explanation</h3>
                            <MdClose className='quizReview__popupHeaderIcon' onClick={() => setShowExplanation(false)} />
                        </div>
                        <Explanation content={questions[questionIndex].explanation}/>
                    </div>
                </div>
                <div className='quizReview__dottedLine'></div>
                {questionIndex < questions.length?   
                    <div className='quizReview__test'>
                        {subject === 'Math' && questions[questionIndex].choices.length > 0
                            ?   <></>
                            :   subject === 'Math'
                            ?   <>
                                    <div className='quizReview__material'>
                                        <div className='quizReview__materialContent'>
                                            <div id='quizReview__passages' className='quizReview__passages'>
                                                <MathOpenEnded />
                                            </div>
                                        </div>
                                    </div>
                                    <div className='quizReview__verticalLine'></div>
                                </>
                            :   <>
                                {subject === 'Reading and Writing' &&
                                    <div
                                        id='quizReview__material'
                                        className='quizReview__material'
                                    >
                                        <div className='quizReview__materialContent'>
                                            <div className='quizReview__passages' id='quizReview__passages'>
                                                {questions[questionIndex].figures.map(figure => (
                                                    <img
                                                        className='quizReview__figure'
                                                        key={figure.id}
                                                        src={figure.image}
                                                        alt={(questionIndex + 1).toString()}
                                                    />
                                                ))}
                                                {questions[questionIndex].passages.map(({ id, passage }) => (
                                                    <div key={id} className='quizReview__passage'>
                                                        <p
                                                            id={id}
                                                            className='katex quizReview__passageText'                                                        >
                                                            {passage}
                                                        </p>
                                                        {annotations.map((annotation, a) => <>
                                                            <div
                                                                // className='katex quizReview__passageAnnotation'
                                                                className={`katex quizReview__passageAnnotation
                                                                    ${annotation.color && 'quizReview__passageAnnotation--' + COLORS[annotation.color]}
                                                                    ${annotation.underline && 'quizReview__passageAnnotation--' + UNDERLINE[annotation.underline]}
                                                                `}
                                                                onMouseOver={(event) => handleAnnotationMouseOver(event, `quizReview__passageAnnotationBubble${a}`)}
                                                                onMouseOut={(event) => handleAnnotationMouseOut(event, `quizReview__passageAnnotationBubble${a}`)}
                                                                dangerouslySetInnerHTML={{ __html: annotation.html }}
                                                            ></div>
                                                            {annotation.annotation &&
                                                                <p
                                                                    id={`quizReview__passageAnnotationBubble${a}`}
                                                                    className='quizReview__passageAnnotationBubble'
                                                                >
                                                                    {annotation.annotation}
                                                                </p>
                                                            }
                                                        </>)}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                }
                                {subject === 'Reading and Writing' && <div className='quizReview__verticalLine'></div>}
                            </>}
                        <div className='quizReview__question' id='quizReview__question'>
                            <div className='quizReview__questionContent'>
                                <div className='quizReview__questionHeader'>
                                    <div className='quizReview__questionHeaderLeft'>
                                        <p className={answers[questionIndex].isCorrect
                                            ? 'quizReview__questionNumber--correct'
                                            : 'quizReview__questionNumber--wrong'
                                        }>
                                            {questionIndex + 1}
                                        </p>
                                        <button className='quizReview__questionMarkButton' onClick={() => {}}>
                                            {answers[questionIndex].marked
                                                ? <MdOutlineBookmark className='quizReview__questionMarkIcon--marked' />
                                                : <MdOutlineBookmarkBorder className='quizReview__questionMarkIcon' />
                                            }
                                            Mark for review
                                        </button>
                                    </div>
                                    <div className='quizReview__questionHeaderRight'>
                                        <button
                                            className={showAnswer
                                                ? 'quizReview__showAnswer--active'
                                                : 'quizReview__showAnswer'
                                            }
                                            onClick={() => setShowAnswer(!showAnswer)}
                                        >
                                            {showAnswer ? 'Hide answer' : 'Show answer'}
                                        </button>
                                        <button
                                            className={showExplanation
                                                ? 'quizReview__showAnswer--active'
                                                : 'quizReview__showAnswer'
                                            }
                                            onClick={() => {
                                                if (calculatorOpen) setCalculatorOpen(false);
                                                if (shortcutsOpen) setShortcutsOpen(false);
                                                if (referenceOpen) setReferenceOpen(false);
                                                setShowExplanation(!showExplanation);
                                            }}
                                        >
                                            Explanation
                                        </button>
                                    </div>
                                </div>
                                <div className='quizReview__questionChoices' id='quizReview__questionChoices'>
                                    {subject === 'Math' && 
                                        questions[questionIndex].figures.map(figure => (
                                            <img
                                                className='quizReview__figure'
                                                key={figure.id}
                                                src={figure.image}
                                                alt={(questionIndex + 1).toString()}
                                            />
                                        ))
                                    }
                                    <p className='quizReview__questionQuestion'>
                                        <span className='katex'>
                                            {questions[questionIndex].question}
                                        </span>
                                    </p>
                                    {questions[questionIndex].choices.length
                                    ?   <>
                                            {questions[questionIndex].choices.map((choice, i) => (
                                                <div key={choice.id} className='quizReview__questionChoiceWrapper'>
                                                    <button
                                                        className={showAnswer && choice.isCorrect
                                                            ? 'quizReview__questionChoice--correct'
                                                            : answers[questionIndex].answer == choice.id
                                                            ? (choice.isCorrect 
                                                                ? 'quizReview__questionChoice--selected'
                                                                : 'quizReview__questionChoice--wrong'
                                                            )
                                                            : 'quizReview__questionChoice'
                                                        }
                                                        onClick={() => {}}
                                                    >
                                                        <div className='quizReview__questionChoiceOutline'></div>
                                                        <p className='quizReview__questionChoiceLetter'>
                                                            {MULTIPLE_CHOICES[i]}
                                                        </p>
                                                        {choice.figure
                                                        ?   <img
                                                                className='quizReview__questionChoiceFigure'
                                                                key={choice.figure}
                                                                src={choice.figure}
                                                                alt={choice.figure}
                                                            />
                                                        :   <p className='quizReview__questionChoiceText'>
                                                                <span className='katex'>
                                                                    {choice.choice}
                                                                </span>
                                                            </p>
                                                        }
                                                    </button>
                                                </div>
                                            ))}
                                        </>
                                    :   <div className='quizReview__questionAnswerReview'>
                                            <TextInput
                                                className='quizReview__questionAnswer'
                                                inputClassName='quizReview__questionAnswerInput'
                                                // name={moduleName + questionIndex}
                                                name={quizName+questionIndex}
                                                value={answers[questionIndex].answer
                                                    ? answers[questionIndex].answer
                                                    : ''
                                                }
                                                onChange={() => {}}
                                            />
                                            {showAnswer &&
                                                <p className='quizReview__questionCorrectAnswer'>
                                                    {questions[questionIndex].correctAnswers.toString().replaceAll(',', ', ')}
                                                </p>
                                            }
                                        </div>
                                    }
                                    <div className='quizReview__marginBottom'></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    :   
                    <div className='quizReview__review'>
                        <h2 className='quizReview__reviewTitle'>
                            Check your work
                        </h2>
                        <p className='quizReview__reviewText'>
                            On test day, you won’t be able to move on to the next module until time expires. 
                            <br/>
                            For these practice questions, you can click next when you’re ready to move on.
                        </p>
                        <div className='quizReview__reviewLegends'>
                            <h3 className='quizReview__reviewModuleName'>{quizName} Questions</h3>
                            <div className='quizReview__reviewLegend'>
                                <div className='quizReview__reviewLegendUnanswered'></div>
                                <p className='quizReview__reviewLegendText'>
                                    Unanswered
                                </p>
                            </div>
                            <div className='quizReview__reviewLegend'>
                                <MdOutlineBookmark className='quizReview__reviewLegendMarked' />
                                <p className='quizReview__reviewLegendText'>
                                    For review
                                </p>
                            </div>
                        </div>
                        <div className='quizReview__reviewItems'>
                            {questions.map((question, i) => (
                                <button
                                    key={question.id}
                                    className={answers[i].answer
                                        ? 'quizReview__reviewItem--answered'
                                        : 'quizReview__reviewItem'
                                    }
                                    onClick={() => {
                                        goToQuestion(i);
                                        setNavigatorOpen(false);
                                    }}
                                >
                                    {questionIndex === i
                                        ? <u className='quizReview__reviewItemNumber'>{i + 1}</u>
                                        : <p className='quizReview__reviewItemNumber'>{i + 1}</p>
                                    }
                                    {questionIndex === i && <MdOutlineLocationOn className='quizReview__reviewItemCurrent' />}
                                    {answers[i].marked && <MdOutlineBookmark className='quizReview__reviewItemMarked' />}
                                </button>
                            ))}
                        </div>
                    </div>
                }
                    <div className='quizReview__dottedLine'></div>
                    <div className='quizReview__footer'>
                        <p className='quizReview__tester'>
                            {studentName}
                        </p>
                        {questionIndex < questions.length &&
                            <button
                                className='quizReview__navigatorToggle'
                                onClick={() => setNavigatorOpen(!navigatorOpen)}
                            >
                                Question {questionIndex + 1}/{questions.length}
                                {navigatorOpen
                                    ? <MdKeyboardArrowDown className='quizReview__navigatorToggleIcon' />
                                    : <MdKeyboardArrowUp className='quizReview__navigatorToggleIcon' />
                                }
                            </button>
                        }
                        <div className='quizReview__buttons'>
                            <Button
                                className='quizReview__button'
                                secondary
                                disabled={questionIndex === 0}
                                onClick={prevQuestion}
                                // onClick={() => goToQuestion(questionIndex - 1)}
                            >
                                BACK
                            </Button>
                            <Button
                                className='quizReview__button'
                                // onClick={() => questionIndex >= questions.length
                                //     ? handleSubmit()
                                //     : goToQuestion(questionIndex + 1)
                                // }
                                onClick={nextQuestion}
                            >
                                NEXT
                            </Button>
                        </div>
                        {navigatorOpen &&
                            <div className='quizReview__navigator' ref={navigatorRef}>
                                <div className='quizReview__navigatorBubble'>
                                    <h2 className='quizReview__navigatorTitle'>
                                        {quizName} Questions
                                    </h2>
                                    <div className='quizReview__navigatorLegends'>
                                        <div className='quizReview__navigatorLegend'>
                                            <MdOutlineLocationOn className='quizReview__navigatorLegendCurrent' />
                                            <p className='quizReview__navigatorLegendText'>
                                                Current
                                            </p>
                                        </div>
                                        <div className='quizReview__navigatorLegend'>
                                            <div className='quizReview__navigatorLegendCorrect'></div>
                                            <p className='quizReview__navigatorLegendText'>
                                                Correct
                                            </p>
                                        </div>
                                        <div className='quizReview__navigatorLegend'>
                                            <div className='quizReview__navigatorLegendWrong'></div>
                                            <p className='quizReview__navigatorLegendText'>
                                                Incorrect
                                            </p>
                                        </div>
                                        <div className='quizReview__navigatorLegend'>
                                            <MdOutlineBookmark className='quizReview__navigatorLegendMarked' />
                                            <p className='quizReview__navigatorLegendText'>
                                                For review
                                            </p>
                                        </div>
                                    </div>
                                    <div className='quizReview__navigatorItems'>
                                        {questions.map((question, i) => (
                                            <button
                                                key={`${question.id}nav`}
                                                className={answers[i].isCorrect
                                                   ? 'quizReview__navigatorItem--correct'
                                                    : 'quizReview__navigatorItem--wrong'
                                                }
                                                onClick={() => {
                                                    goToQuestion(i);
                                                    setNavigatorOpen(false);
                                                }}
                                            >
                                                {questionIndex === i
                                                    ? <u className='quizReview__navigatorItemNumber'>{i + 1}</u>
                                                    : <p className='quizReview__navigatorItemNumber'>{i + 1}</p>
                                                }
                                                {questionIndex === i && <MdOutlineLocationOn className='quizReview__navigatorItemCurrent' />}
                                                {answers[i].marked && <MdOutlineBookmark className='quizReview__navigatorItemMarked' />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        }
                    </div> 


                {/* <TestModule
                    // moduleName={moduleName}
                    quizName={quizName}
                    subject={moduleSubject}
                    // timeLeft={moduleTime}
                    prompt={quizPrompt}
                    questions={questions}
                    questionIndex={questionIndex}
                    answers={answers}
                    markQuestion={markQuestion}
                    crossOutChoice={crossOutChoice}
                    saveAnnotation={saveAnnotation}
                    deleteAnnotation={deleteAnnotation}
                    handleChange={value => setAnswer(value)}
                    handleSelect={value => setChoice(value)}
                    navigateQuestion={navigateQuestion}
                    handleSaveAndExit={handleSaveAndExit}
                    handleDiscardAndExit={handleDiscardAndExit}
                    handleSubmit={handleSubmit}
                /> */}
            </div>
        </div>
    )
}

export default QuizReview;